import { DocumentProvider, DocumentProviderCapabilities, DocumentProviderType } from 'models/documentProvider';
import { apiGetDocumentProviders } from 'services/Api/documentProviderService';
import { apiRequest } from 'services/Auth/authConfig';
import { EntityCache, ICache } from './entityCache';

/**
 * The capability rows the whole library gates its affordances on.
 *
 * getCapabilities never returns undefined, and that is deliberate: a gate must not flip while
 * the cache is still loading. SharePoint falls back to all-true, which is the behaviour that
 * existed before providers, and every other provider falls back to all-false, so nothing is
 * offered for content we cannot describe yet.
 */
export class DocumentProviderCache
  extends EntityCache<DocumentProvider>
  implements ICache<DocumentProvider, DocumentProviderCache>
{
  private isLoading: boolean = false;

  private providersPromise: Promise<DocumentProvider[]> | undefined;

  constructor() {
    super();
    this.getId = (provider) => provider.providerType;
    this.getEmptyItem = () => new DocumentProvider();
  }

  getCapabilities(providerType: DocumentProviderType): DocumentProviderCapabilities {
    //SharePoint content is reached through Microsoft Graph with the user's own token: a user who signed in
    //with another identity provider has none, so every affordance is off and the rows stay listed. This one
    //gate is what switches the pickers, uploads, previews and edits off for them
    if (providerType === DocumentProviderType.SharePoint && this.appContext && !this.appContext.hasMicrosoftIdentity()) {
      return new DocumentProviderCapabilities();
    }

    const provider = this.items?.find((p) => p.providerType === providerType);

    if (provider) {
      return provider.capabilities;
    }

    return DocumentProviderCache.getFallbackCapabilities(providerType);
  }

  /** SharePoint keeps every affordance it had; an unknown provider offers nothing. */
  static getFallbackCapabilities(providerType: DocumentProviderType): DocumentProviderCapabilities {
    if (providerType === DocumentProviderType.SharePoint) {
      return new DocumentProviderCapabilities(true);
    }

    if (providerType === DocumentProviderType.Virtual) {
      const capabilities = new DocumentProviderCapabilities();
      capabilities.download = true;
      capabilities.embedPreview = true;
      capabilities.webLink = true;
      capabilities.publish = true;
      capabilities.approvals = true;
      capabilities.readConfirmations = true;
      capabilities.wholeList = true;

      return capabilities;
    }

    return new DocumentProviderCapabilities();
  }

  async getItems(refresh: boolean = false): Promise<DocumentProvider[]> {
    if (this.isLoading && this.providersPromise) {
      return await this.providersPromise;
    }

    if (this.items.length === 0 || refresh) {
      if (this.appContext && !this.isLoading) {
        try {
          this.isLoading = true;
          const accessToken = await this.appContext.getAccessToken(apiRequest.scopes);
          this.providersPromise = apiGetDocumentProviders(accessToken);
          this.items = await this.providersPromise;
        } catch (err) {
          this.appContext.setError(err);
        } finally {
          this.providersPromise = undefined;
          this.isLoading = false;
        }
      } else if (!this.appContext) {
        this.items = [];
      }
    }

    return this.items;
  }

  clone(): DocumentProviderCache {
    const cache: DocumentProviderCache = new DocumentProviderCache();
    cache.items = this.items?.map((p) => {
      const copy = new DocumentProvider();
      copy.providerType = p.providerType;
      copy.name = p.name;
      copy.capabilities = { ...p.capabilities };
      copy.enabled = p.enabled;

      return copy;
    });

    return cache;
  }
}
